import { useSession } from '@/features/auth/api/useSession'
import { CAP, can } from '@/lib/can'

/* Suppression is handled in two places, both deliberate: contactToPayload
 * turns a withheld value into an empty string on the way into a draft, and
 * ContactsEditor disables the input. Both together keep the MARKER from being
 * written back over a real number (AD-6) — but not the blank: a draft saved
 * with '' in place of a hidden phone would replace the stored one with nothing.
 * The server closes that (B4): an update from a caller without
 * `directory.contact.read` keeps the stored contacts and ignores the ones in
 * the request. This hook is its visible half. */

/** Whether a record sheet's contacts block is editable.
 *
 * Computed inside the sheets rather than passed in, so none of the hosts that
 * mount one — the clients page, the client card, the counterparty page, the
 * three pickers — can forget it. A NEW record is always editable: there is
 * nothing stored to overwrite (B4, L2). An existing one is editable only for a
 * caller who holds the contact key; anyone else would type into a list the
 * server ignores. Before the session answers, `can()` is false, so the block
 * starts read-only rather than offering inputs that then lock.
 *
 * Its own module since the counterparty sheet (S-22b) became the second
 * component file needing it: a hook exported from a component file breaks fast
 * refresh for that file. */
export function useContactsEditable(record: { id: string } | undefined): boolean {
  const { data: user } = useSession()
  return !record || can(user, CAP.directoryContactRead)
}
